import type { OwnerResource } from "../../owner";
import type { CustomerGroupResource } from "../../customer-group";
import type { TextTemplatePlaceholder } from "../../../common.ts";

/**
 * Types for Anlass campaigns (backend modules/Marketing).
 *
 * Conventional resource: /api/anlass-campaign (list, show, store, update,
 * destroy, new-form). Owner-scoped like recommendation campaigns: the list
 * narrows to `owner_id` (query or grid filter).
 *
 * Action endpoints:
 * - GET  /anlass-campaign/placeholders?anlass_type=… — editor placeholders;
 * - GET  /anlass-campaign/{id}/activation-check;
 * - POST /anlass-campaign/{id}/activate | pause | resume | archive;
 * - POST /anlass-campaign/{id}/test-run — send the draft to the test group.
 */

export enum AnlassTypeEnum {
  Birthday = "birthday",
}

export enum AnlassCampaignStatusEnum {
  Draft = "draft",
  Active = "active",
  Paused = "paused",
  Archived = "archived",
}

export enum FrequencyPolicyEnum {
  /** The occasion is sent regardless of other recent campaign messages. */
  Bypass = "bypass",
  /** No message if the customer got another one within the quiet period. */
  QuietPeriod = "quiet_period",
}

export interface AnlassTriggerConfig {
  /** 29.02 is greeted on 28.02 in non-leap years. */
  leap_day_policy: "feb28";
  /** A late send is allowed until the end of the occasion day. */
  late_tolerance: "end_of_day";
}

export interface AnlassFrequencyConfig {
  policy: FrequencyPolicyEnum;
}

interface AnlassChannelConfigBase {
  enabled: boolean;
}

/** Send time "HH:MM" in the campaign time zone. */
export type AnlassSendTime = string;

export interface AnlassEmailChannelConfig extends AnlassChannelConfigBase {
  send_time: AnlassSendTime;
  owner_profile_id: string | null;
  subject: string | null;
  body_html: string | null;
  body_plain: string | null;
  /** HTML edited in the code editor instead of CKEditor. */
  use_raw_html: boolean;
}

export interface AnlassSmsChannelConfig extends AnlassChannelConfigBase {
  send_time: AnlassSendTime;
  owner_profile_id: string | null;
  /** Variants; one is chosen per execution, deterministically. */
  texts: string[];
}

export interface AnlassWhatsAppChannelConfig extends AnlassChannelConfigBase {
  send_time: AnlassSendTime;
  owner_profile_id: string | null;
  provider: "wazzup";
  external_template_id: string | null;
  template_name: string | null;
  template_version: string | null;
  /** Placeholder tokens by template position. */
  variables: string[];
}

export interface AnlassPostalChannelConfig extends AnlassChannelConfigBase {
  /** Days before the occasion the letter must arrive, -60..0. */
  delivery_offset_days: number;
  template_id: string | null;
  /** Letter text with placeholders; the PDF is built from it. */
  letter_text: string | null;
  /** Owner profile whose address is printed as the sender. */
  owner_profile_id: string | null;
}

/**
 * `draft_config` of the campaign; the published version is an immutable
 * snapshot of it. Unknown keys are rejected by the backend.
 */
export interface AnlassCampaignConfig {
  schema_version: 1;
  trigger: AnlassTriggerConfig;
  frequency: AnlassFrequencyConfig;
  /** Gutschein-Pool; null — campaign without a code. */
  coupon_pool_id: string | null;
  email: AnlassEmailChannelConfig;
  sms: AnlassSmsChannelConfig;
  whatsapp: AnlassWhatsAppChannelConfig;
  print_mailing: AnlassPostalChannelConfig;
  epost: AnlassPostalChannelConfig;
}

/**
 * Channels of the published version (draft if never activated), last sent
 * and next planned message, failed channels in the last 24 h. Test runs are
 * not counted.
 */
export interface AnlassCampaignOverview {
  channels: string[];
  last_sent_at: string | null;
  next_planned_at: string | null;
  failed_last_24h: number;
}

export interface AnlassCampaignResource {
  id: string;
  owner_id: string;
  name: string;
  anlass_type: AnlassTypeEnum;
  status: AnlassCampaignStatusEnum;
  timezone: string;
  active_from: string | null;
  active_to: string | null;
  test_customer_group_id: string | null;
  draft_config: AnlassCampaignConfig;
  /** null until the first activation. */
  current_version_id: string | null;
  activated_at: string | null;
  paused_at: string | null;
  archived_at: string | null;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
  /** Card only: the published version that is being sent. */
  current_version?: {
    id: string;
    version_no: number;
    /** Number among published versions; test snapshots share version_no. */
    published_no: number;
    created_at: string;
  } | null;
  /** Card only: the draft differs from the published version. */
  draft_changed?: boolean;
  /** List and card: what the campaign actually does. */
  overview?: AnlassCampaignOverview;
  /** include=owner */
  owner?: OwnerResource;
  /** include=testCustomerGroup */
  test_customer_group?: CustomerGroupResource | null;
}

/**
 * Config as sent to the backend. Missing keys fall back to their defaults —
 * NOT to the stored value: the backend rebuilds the whole draft from what it
 * receives. A form must therefore always send the complete config.
 */
export type AnlassCampaignConfigInput = {
  [Key in keyof AnlassCampaignConfig]?: AnlassCampaignConfig[Key] extends object
    ? Partial<AnlassCampaignConfig[Key]>
    : AnlassCampaignConfig[Key];
};

export interface AnlassCampaignStoreRequest {
  owner_id: string;
  name: string;
  anlass_type: AnlassTypeEnum;
  timezone?: string;
  active_from?: string | null;
  active_to?: string | null;
  test_customer_group_id?: string | null;
  draft_config?: AnlassCampaignConfigInput;
}

/** Owner, type and status cannot be changed by an update. */
export type AnlassCampaignUpdateRequest = Partial<
  Omit<AnlassCampaignStoreRequest, "owner_id" | "anlass_type">
>;

export interface AnlassActivationIssue {
  /** Machine code, e.g. coupon_pool_low, legacy_birthday_active. */
  code: string;
  /** Field path, e.g. draft_config.email.subject. */
  field: string;
  message: string;
}

export interface AnlassActivationCheck {
  passes: boolean;
  errors: AnlassActivationIssue[];
  warnings: AnlassActivationIssue[];
}

export interface AnlassTestRunResult {
  test_run_id: string;
  version_id: string;
  recipients: number;
}

/** Editor placeholder; `requires_coupon` — works only with a Gutschein-Pool. */
export type AnlassPlaceholder = TextTemplatePlaceholder & {
  requires_coupon: boolean;
};
